import { useEffect, useState } from "react";

export interface UseOptimalPopoverSideArgs {
  /**
   * The minimum permitted height of the popover content. This is used to determine if there is
   * enough space on the default side ("below") to display the content.
   */
  minHeight: number;

  /**
   * Flag indicating whether the popover position should be recalculated. Internally, this is passed
   * as a dependency to a `useEffect`. Can set this to true when loading finishes, when the number
   * of items changes, etc.
   */
  shouldRecalculate: boolean;

  /**
   * A ref to the popover trigger element.
   */
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}

export interface UseOptimalPopoverSideReturn {
  /**
   * The optimal side for the popover to be positioned. `undefined` means the popover should be
   * positioned by Radix.
   */
  side: "top" | "bottom" | undefined;
}

/**
 * Hook that calculates the optimal side for a `RegrelloFormFieldSelect` popover to be positioned
 * based on available space. Assumes "top" or "bottom" are the only valid sides.
 */
export function useOptimalPopoverSide({
  minHeight,
  shouldRecalculate,
  triggerRef,
}: UseOptimalPopoverSideArgs): UseOptimalPopoverSideReturn {
  const [side, setSide] = useState<"top" | "bottom" | undefined>();

  // Calculate optimal popover side based on available space.
  useEffect(() => {
    if (!shouldRecalculate || triggerRef.current == null) {
      return;
    }

    const calculateOptimalSide = () => {
      const triggerRect = triggerRef.current?.getBoundingClientRect();

      if (triggerRect == null) {
        return;
      }

      const availableSpaceAbove = triggerRect.top;
      const availableSpaceBelow = window.innerHeight - triggerRect.bottom;

      // If there's sufficient space below, use default positioning. (Note: This may cause problems
      // in Radix due to switching between controlled and uncontrolled usage, but it seems okay.)
      if (availableSpaceBelow >= minHeight) {
        setSide(undefined);
        return;
      }

      // Position on "top" if there's more space above and insufficient space below.
      if (availableSpaceAbove > availableSpaceBelow) {
        setSide("top" as const);
        return;
      }

      // Otherwise, let Radix decide.
      setSide(undefined);
    };

    // Recalculate when content changes.
    calculateOptimalSide();

    // Also recalculate when the window resizes.
    window.addEventListener("resize", calculateOptimalSide);
    return () => {
      window.removeEventListener("resize", calculateOptimalSide);
    };
  }, [shouldRecalculate, minHeight, triggerRef]);

  return { side };
}
